import { yupResolver } from '@hookform/resolvers';
import { Button } from '@material-ui/core';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import InputField from '../../components/Inputs/TextField';
import LoadingContainer from '../../components/LoadingContainer';
import { api } from '../../core/api';
import { HelpRequest } from '../../generated/model/helpRequest';
import { useSnackbarMessage } from '../../hooks/useSnackbarMessage';
import { PRODUCT_NAME } from './Help';

interface RequestAccessForm {
  name: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  message: string;
}

const HelpPageForm: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [sentEmail, setSentEmail] = useState<boolean | null>(null);
  const { register, handleSubmit, errors } = useForm<RequestAccessForm>({
    resolver: yupResolver(FormSchema),
    reValidateMode: 'onSubmit',
  });

  const onSubmit = async (formData: RequestAccessForm) => {
    try {
      setIsLoading(true);
      setSentEmail(null);

      const request: HelpRequest = {
        productName: PRODUCT_NAME,
        ...formData,
      };
      const response = await api.help.sendHelpRequest(request);
      if (response) {
        setSentEmail(true);
      }
    } catch (error) {
      setSentEmail(false);
    } finally {
      setIsLoading(false);
    }
  };

  useSnackbarMessage(!!sentEmail, 'app:helpForm.sentEmail', {
    variant: 'success',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right',
    },
  });

  useSnackbarMessage(sentEmail === false, 'error:defaultError', {
    variant: 'error',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <InputField
        label={t('app:helpForm.name')}
        name="name"
        mandatory={true}
        refReg={register}
        errors={errors}
      />
      <InputField
        label={t('app:helpForm.companyName')}
        name="companyName"
        refReg={register}
        errors={errors}
      />
      <InputField
        label={t('app:helpForm.email')}
        name="email"
        mandatory={true}
        refReg={register}
        errors={errors}
      />
      <InputField
        label={t('app:helpForm.phoneNumber')}
        name="phoneNumber"
        refReg={register}
        errors={errors}
      />
      <InputField
        label={t('app:helpForm.message')}
        name="message"
        mandatory={true}
        multiline={true}
        rows={4}
        refReg={register}
        errors={errors}
      />
      <Button type="submit" color="primary" variant="contained" disabled={isLoading}>
        {isLoading ? <LoadingContainer size={20} /> : t('app:send')}
      </Button>
    </form>
  );
};

const FormSchema: any = yup.object().shape({
  name: yup.string().trim().required('app:helpForm.nameRequired'),
  email: yup.string().email('app:helpForm.emailValid').required('app:helpForm.emailRequired'),
  message: yup.string().trim().required('app:helpForm.messageRequired'),
});

export default HelpPageForm;
